<template>
  <div class="location-card" :class="customClass" :style="animationStyle">
    <div class="location-header" v-if="name">
      <h3 class="location-name">{{ name }}</h3>
    </div>
    
    <div class="location-details">
      <div class="location-detail-item" v-if="address">
        <i class="fas fa-map-marker-alt location-icon"></i>
        <span class="location-text">{{ address }}</span>
      </div>
      
      <div class="location-detail-item" v-if="phone">
        <i class="fas fa-phone location-icon"></i>
        <a :href="`tel:${phone}`" class="location-link">{{ phone }}</a>
      </div>
      
      <div class="location-detail-item" v-if="email">
        <i class="fas fa-envelope location-icon"></i>
        <a :href="`mailto:${email}`" class="location-link">{{ email }}</a>
      </div>
      
      <div class="location-detail-item" v-if="hours">
        <i class="fas fa-clock location-icon"></i>
        <span class="location-text">{{ hours }}</span>
      </div>
      
      <div class="location-detail-item" v-if="days">
        <i class="fas fa-calendar-alt location-icon"></i>
        <span class="location-text">{{ days }}</span>
      </div>
    </div>
    
    <div class="location-actions" v-if="googleMapsUrl">
      <a 
        :href="googleMapsUrl" 
        target="_blank" 
        rel="noopener noreferrer"
        class="btn btn-location-directions"
      >
        <i class="fas fa-directions mr-2"></i>
        ¿Cómo llegar?
      </a>
    </div>
    
    <slot></slot>
  </div>
</template>

<script>
export default {
  name: "LocationCard",
  props: {
    name: {
      type: String,
      default: ""
    },
    address: {
      type: String,
      default: ""
    },
    phone: {
      type: String,
      default: ""
    },
    email: {
      type: String,
      default: ""
    },
    hours: {
      type: String,
      default: ""
    },
    days: {
      type: String,
      default: ""
    },
    googleMapsUrl: {
      type: String,
      default: ""
    },
    animationDelay: {
      type: Number,
      default: 0
    },
    customClass: {
      type: String,
      default: "animate-on-scroll fade-in-up"
    }
  },
  computed: {
    animationStyle() {
      return this.animationDelay > 0 
        ? { 'animation-delay': `${this.animationDelay}s` }
        : {};
    }
  }
};
</script>

<style lang="scss" scoped>
@import '../styles/variables';

.location-card {
  background: white;
  border-radius: 15px;
  padding: 2rem;
  text-align: center;
  box-shadow: 0 5px 20px $landing-primary-rgba-08;
  transition: all 0.3s ease;
  border: 2px solid $landing-border-light;
  height: 100%;
  display: flex;
  flex-direction: column;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 30px rgba(40, 167, 69, 0.2);
    border-color: $landing-primary-color;
  }
  
  .location-header {
    margin-bottom: 1.5rem;
    padding-bottom: 1.5rem;
    border-bottom: 2px solid #F3F6F9;
    
    .location-name {
      font-size: 1.75rem;
      font-weight: 700;
      color: #2D4A4D;
      margin: 0;
    }
  }
  
  .location-details {
    flex: 1;
    margin-bottom: 1.5rem;
    text-align: left;
    
    .location-detail-item {
      display: flex;
      align-items: flex-start;
      gap: 1rem;
      margin-bottom: 1rem;
      
      &:last-child {
        margin-bottom: 0;
      }
      
      .location-icon {
        color: $landing-primary-color;
        font-size: 1.1rem;
        margin-top: 0.2rem;
        flex-shrink: 0;
        width: 20px;
        text-align: center;
      }
      
      .location-text {
        font-size: 1rem;
        color: #2D4A4D;
        line-height: 1.6;
        flex: 1;
      }
      
      .location-link {
        font-size: 1rem;
        color: $landing-primary-color;
        text-decoration: none;
        transition: all 0.3s ease;
        line-height: 1.6;
        
        &:hover {
          color: $landing-primary-dark;
          text-decoration: underline;
        }
      }
    }
  }
  
  .location-actions {
    padding-top: 1.5rem;
    border-top: 2px solid #F3F6F9;
    
    .btn-location-directions {
      width: 100%;
      background: $landing-primary-gradient;
      color: white;
      border: none;
      border-radius: 12px;
      padding: 14px 24px;
      font-size: 1.1rem;
      font-weight: 600;
      transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
      box-shadow: 0 8px 25px $landing-primary-rgba-30;
      text-decoration: none;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      
      &:hover {
        transform: translateY(-3px);
        box-shadow: 0 12px 35px $landing-primary-rgba-40;
        background: $landing-primary-gradient-dark;
        color: white;
        text-decoration: none;
      }
    }
  }
}

@media (max-width: 768px) {
  .location-card {
    padding: 1.5rem;
    
    .location-header .location-name {
      font-size: 1.3rem;
    }
    
    .location-details .location-detail-item {
      .location-text,
      .location-link {
        font-size: 0.9rem;
      }
    }
  }
}
</style>
